// src/lib/names.js
// STATUS: active | UPDATED: 2026-08-28
// LOG:
//   2026-08-28  New. One answer to "what do we call this person".
//
// Five places asked that question and four of them answered differently:
//
//   Header            display_name -> full_name -> email
//   Settings          full_name -> display_name          (reversed)
//   Dashboard, Coach  first_name -> display_name         (chosen name loses)
//   Team              display_name -> first_name -> email
//
// So a salesperson who set their display name to "Rocky" was greeted as
// "Richard" on the dashboard, "Rocky" in the header, and "Richard Alvarez" in
// settings, on the same screen refresh. Initials were worse: the fallback was
// 'JT' on the dashboard, 'T' in the greeting, 'U' in settings and a two letter
// slice in the header. 'JT' was a developer's own initials, hardcoded.
//
// THE RULE: display_name wins. It is the only field the person chose for
// themselves, and it is literally the answer to "what would you like to be
// called". Everything else is a fallback for when they have not said.
//
// Email is the last resort before a generic, and only the part before the @,
// because showing somebody their own address back as their name is worse than
// showing them nothing.

// The full name to show in a header, an account panel, a team roster.
export function displayNameFor(profile, user) {
  return (
    profile?.display_name?.trim() ||
    profile?.full_name?.trim() ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim() ||
    user?.email?.split('@')[0] ||
    'there'
  )
}

// The short name for greetings: "Good morning, Rocky."
// If they chose a display name, use its first word rather than their legal
// first name, because choosing "Rocky" is a request not to be called Richard.
export function firstNameFor(profile, user) {
  const chosen = profile?.display_name?.trim()
  if (chosen) return chosen.split(/\s+/)[0]
  const first = profile?.first_name?.trim()
  if (first) return first
  const full = profile?.full_name?.trim()
  if (full) return full.split(/\s+/)[0]
  return user?.email?.split('@')[0] || null
}

// Avatar initials. Stored value wins; otherwise derive from whatever name we
// landed on. Never a hardcoded pair of letters belonging to a real person.
export function initialsFor(profile, user) {
  const stored = profile?.initials?.trim()
  if (stored) return stored.toUpperCase()

  const name = displayNameFor(profile, user)
  if (!name || name === 'there') return '?'

  const words = name.split(/\s+/).filter(Boolean)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}
